/**
 * CheckpointList Component (AC-571)
 *
 * Displays a list of project checkpoints with actions for restore, delete,
 * and compare functionality.
 *
 * Parent Component: HistoryPage (left panel)
 *
 * Features:
 * - Display checkpoint rows with name (or "Auto-checkpoint" for is_auto)
 * - Show description, entity counts (models, agents, workflows)
 * - Show relative time
 * - Restore and Delete buttons
 * - Toggle to show/hide auto-checkpoints
 * - Checkbox selection for compare feature
 */

import React, { useState } from 'react';
import { RotateCcw, Trash2 } from 'lucide-react';
import { useVersionStore } from '../../store/versionStore';
import { CheckpointSummary } from '../../types/version_control';
import RestoreConfirmModal from './RestoreConfirmModal';
import Tooltip from '../common/Tooltip';

// Helper function to format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return 'just now';
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  } else if (diffDay === 1) {
    return 'yesterday';
  } else if (diffDay < 30) {
    return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  } else {
    const diffMonth = Math.floor(diffDay / 30);
    return `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`;
  }
}

interface CheckpointListProps {
  checkpoints: CheckpointSummary[];
  projectId: number;
  onRestoreComplete: () => void;
  loading?: boolean;
  error?: string | null;
}

const CheckpointList: React.FC<CheckpointListProps> = ({
  checkpoints,
  projectId,
  onRestoreComplete,
  loading = false,
  error = null,
}) => {
  const [showAutoCheckpoints, setShowAutoCheckpoints] = useState(true);
  const [selectedCheckpoints, setSelectedCheckpoints] = useState<Set<number>>(new Set());
  const [restoreModalCheckpoint, setRestoreModalCheckpoint] = useState<CheckpointSummary | null>(null);

  const { deleteCheckpoint } = useVersionStore();

  // Filter checkpoints based on auto-checkpoint toggle
  const filteredCheckpoints = showAutoCheckpoints
    ? checkpoints
    : checkpoints.filter((cp) => !cp.is_auto);

  const handleSelectCheckpoint = (checkpointId: number) => {
    setSelectedCheckpoints((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(checkpointId)) {
        newSet.delete(checkpointId);
      } else {
        // Limit to 2 selections for compare
        if (newSet.size >= 2) {
          // Remove the first selected item to make room
          const first = Array.from(newSet)[0];
          newSet.delete(first);
        }
        newSet.add(checkpointId);
      }
      return newSet;
    });
  };

  const handleRestore = (checkpoint: CheckpointSummary) => {
    setRestoreModalCheckpoint(checkpoint);
  };

  const handleRestoreComplete = () => {
    setRestoreModalCheckpoint(null);
    onRestoreComplete();
  };

  const handleDelete = async (checkpointId: number) => {
    // Delete the checkpoint (confirmation handled by optimistic UI or parent)
    await deleteCheckpoint(projectId, checkpointId);
  };

  const handleCompare = () => {
    // Placeholder for compare functionality (AC-572)
    const ids = Array.from(selectedCheckpoints);
    console.log('Compare checkpoints:', ids);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-500 dark:text-gray-400">Loading checkpoints...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  // Empty state
  if (checkpoints.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">No checkpoints yet</p>
        <p className="text-sm text-gray-400 mt-1">
          Create a checkpoint to save your current project state
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with toggle and compare button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <input
            id="show-auto-checkpoints"
            type="checkbox"
            checked={showAutoCheckpoints}
            onChange={(e) => setShowAutoCheckpoints(e.target.checked)}
            className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
          />
          <label htmlFor="show-auto-checkpoints" className="text-sm text-gray-600 dark:text-gray-400">
            Show auto
          </label>
        </div>
        <button
          onClick={handleCompare}
          disabled={selectedCheckpoints.size !== 2}
          className="px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Compare
        </button>
      </div>

      {/* Checkpoint list */}
      <div className="space-y-2">
        {filteredCheckpoints.map((checkpoint) => {
          const displayName = checkpoint.name || 'Auto-checkpoint';
          const isSelected = selectedCheckpoints.has(checkpoint.checkpoint_id);

          return (
            <div
              key={checkpoint.checkpoint_id}
              data-testid="checkpoint-row"
              className={`p-4 bg-white dark:bg-cap-dark border rounded-lg hover:shadow-sm transition-shadow ${
                isSelected ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Selection checkbox */}
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleSelectCheckpoint(checkpoint.checkpoint_id)}
                  className="mt-1 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  aria-label={`Select checkpoint ${checkpoint.checkpoint_id} for compare`}
                />

                {/* Checkpoint info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-warm-white truncate">
                      {displayName}
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                      {formatRelativeTime(checkpoint.created_at)}
                    </span>
                  </div>

                  {checkpoint.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                      {checkpoint.description}
                    </p>
                  )}

                  {/* Entity counts */}
                  {checkpoint.entity_counts && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>{checkpoint.entity_counts.models} models, {checkpoint.entity_counts.agents} agents, {checkpoint.entity_counts.orchestrations} orchestrations</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Tooltip content="Restore">
                    <button
                      onClick={() => handleRestore(checkpoint)}
                      className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                      aria-label="Restore"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  {/* Only show delete for manual checkpoints */}
                  {!checkpoint.is_auto && (
                    <Tooltip content="Delete">
                      <button
                        onClick={() => handleDelete(checkpoint.checkpoint_id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Restore confirmation modal */}
      <RestoreConfirmModal
        isOpen={restoreModalCheckpoint !== null}
        checkpoint={restoreModalCheckpoint}
        projectId={projectId}
        onClose={() => setRestoreModalCheckpoint(null)}
        onRestoreComplete={handleRestoreComplete}
      />
    </div>
  );
};

export default CheckpointList;
